import { useEffect, useRef } from 'react';
import { getFocusableElements } from '@/lib/a11y-utils';

/**
 * Traps keyboard focus within a container element when active.
 * Restores focus to the previously focused element when deactivated.
 *
 * @param containerRef - Ref to the container element to trap focus within
 * @param active - Whether the focus trap is currently active
 */
export function useFocusTrap(containerRef: React.RefObject<HTMLElement | null>, active: boolean) {
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active || !containerRef.current) return;

    // Store the currently focused element to restore later
    previousFocusRef.current = document.activeElement as HTMLElement | null;

    const container = containerRef.current;

    // Focus the first focusable element inside the container
    const focusableElements = getFocusableElements(container);
    if (focusableElements.length > 0) {
      // Small delay to ensure DOM is ready (portals, animations)
      requestAnimationFrame(() => {
        const firstFocusable = getFocusableElements(container)[0];
        firstFocusable?.focus();
      });
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const focusable = getFocusableElements(container);
      if (focusable.length === 0) return;

      const firstElement = focusable[0];
      const lastElement = focusable[focusable.length - 1];

      if (e.shiftKey) {
        // Shift+Tab: if focus is on first element, wrap to last
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab: if focus is on last element, wrap to first
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);

      // Restore focus to the previously focused element (only if still in the DOM)
      if (
        previousFocusRef.current &&
        typeof previousFocusRef.current.focus === 'function' &&
        document.contains(previousFocusRef.current)
      ) {
        previousFocusRef.current.focus();
      }
    };
  }, [active, containerRef]);
}
